/** @format */

import { useEffect, useMemo, useState } from 'react';

import moment from 'jalali-moment';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import Datepicker from '../../Shared/Components/Datepicker';
import Loading from '../../Shared/Components/Loading';
import Pagination from '../../Shared/Components/Pagination';
import { error } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import useQuery from '../../Shared/queryString';
import { number_format } from '../../Shared/utils/helper';
import Master from '../Layouts/Master';

const MyPayments = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState({ data: [], meta: {} });
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('');
  const [trackingId, setTrackingId] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(search);
    setFromDate(params.get('start') || '');
    setToDate(params.get('end') || '');
    setStatus(params.get('status') || '');
    setTrackingId(params.get('tracking_id') || '');
    getPayments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const getPayments = async () => {
    setLoading(true);
    await useApi.get(
      `/panel/my-payments${search}`,
      {},
      (res) => {
        setPayments(res.data);
        setLoading(false);
      },
      () => {
        setLoading(false);
        error('هنگام دریافت ریزتراکنش‌ها خطایی رخ داده است');
      },
    );
  };

  const buildFilterQuery = () => {
    const params = new URLSearchParams();
    if (fromDate) params.set('start', fromDate);
    if (toDate) params.set('end', toDate);
    if (status) params.set('status', status);
    if (trackingId.trim()) params.set('tracking_id', trackingId.trim());
    return params.toString();
  };

  const searchPayments = () => {
    const query = buildFilterQuery();
    navigate(`/panel/profile/my-payments${query ? `?${query}` : ''}`);
  };

  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setStatus('');
    setTrackingId('');
    navigate('/panel/profile/my-payments');
  };

  const paginationBasePath = useMemo(() => {
    const query = useQuery.only('start', 'end', 'status', 'tracking_id');
    return `/panel/profile/my-payments${query}`;
  }, [search]);

  const totalCount = payments?.meta?.pagination?.total ?? 0;

  return loading ? (
    <Loading />
  ) : (
    <Master title='ریزتراکنش‌های من'>
      <div className='d-flex flex-row justify-content-between border-bottom pb-2 my-3'>
        <div className='d-flex flex-row mt-2 align-items-center'>
          <i className='fad fa-receipt f-20 text-secondary mx-2'></i>
          <h1 className='text-secondary f-13 font-weight-bold mt-1 mb-0 p-0'>
            ریزتراکنش‌های من
          </h1>
          <span className='badge badge-secondary f-12 mx-2'>
            {number_format(totalCount)} مورد
          </span>
        </div>
      </div>

      <div className='d-flex flex-column flex-lg-row align-items-stretch align-items-lg-end w-100 my-3'>
        <div className='form-group d-flex flex-column mx-lg-2 mb-2 mb-lg-0'>
          <label className='f-12 mb-1'>از</label>
          <Datepicker
            handleChange={(name, value) => setFromDate(value)}
            name='start'
            value={fromDate}
          />
        </div>
        <div className='form-group d-flex flex-column mx-lg-2 mb-2 mb-lg-0'>
          <label className='f-12 mb-1'>تا</label>
          <Datepicker
            handleChange={(name, value) => setToDate(value)}
            name='end'
            value={toDate}
          />
        </div>
        <div className='form-group d-flex flex-column mx-lg-2 mb-2 mb-lg-0'>
          <label className='f-12 mb-1'>وضعیت</label>
          <select
            className='form-control'
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value=''>همه</option>
            <option value='1'>موفق</option>
            <option value='2'>ناموفق</option>
          </select>
        </div>
        <div className='form-group d-flex flex-column mx-lg-2 mb-2 mb-lg-0 flex-grow-1'>
          <label className='f-12 mb-1'>شماره پیگیری</label>
          <input
            type='text'
            className='form-control'
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchPayments()}
          />
        </div>
        <div className='d-flex flex-row mx-lg-2 mb-2 mb-lg-0'>
          <button
            type='button'
            className='btn btn-info text-white f-12'
            onClick={searchPayments}
          >
            جستجو
          </button>
          <button
            type='button'
            className='btn btn-outline-secondary f-12 mx-2'
            onClick={clearFilters}
          >
            پاک کردن
          </button>
        </div>
      </div>

      <div className='w-100 overflow-auto'>
        {payments?.data?.length > 0 ? (
          <table className='table table-hover'>
            <thead>
              <tr>
                <th>#</th>
                <th>تاریخ</th>
                <th>مبلغ (ریال)</th>
                <th>شماره سفارش</th>
                <th>شماره پیگیری</th>
                <th>توضیحات</th>
                <th>وضعیت</th>
                <th>فاکتور</th>
              </tr>
            </thead>
            <tbody>
              {payments.data.map((item) => (
                <tr key={item.id}>
                  <th>{item.id}</th>
                  <td>
                    {item.created_at
                      ? moment(item.created_at).locale('fa').format('jYYYY/jMM/jDD HH:mm')
                      : '-'}
                  </td>
                  <td>{number_format(item.amount)}</td>
                  <td>{item.order_id || '-'}</td>
                  <td>{item.tracking_id || '-'}</td>
                  <td>{item.description || '-'}</td>
                  <td>
                    {item.status === 2 ? (
                      <span className='badge badge-danger badge-pill p-2'>ناموفق</span>
                    ) : item.status === 1 ? (
                      <span className='badge badge-success badge-pill p-2'>موفق</span>
                    ) : (
                      <span className='badge badge-secondary badge-pill p-2'>—</span>
                    )}
                  </td>
                  <td>
                    {item.has_factor && item.status === 1 ? (
                      <Link
                        to={`/panel/matches/financial-management/show/${item.paymentable_id}`}
                      >
                        مشاهده
                      </Link>
                    ) : (
                      <span className='text-muted f-12'>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className='d-flex flex-row justify-content-center border p-3 my-3'>
            <span className='f-13 font-weight-bold text-secondary'>
              تراکنشی یافت نشد
            </span>
          </div>
        )}
      </div>

      <div className='w-100 mt-4 d-flex'>
        <Pagination
          totalPage={payments?.meta?.pagination?.total_pages}
          basePath={paginationBasePath}
        />
      </div>
    </Master>
  );
};

export default MyPayments;
